export enum Gender {
  M = "M",
  F = "F",
  Other = "Other",
}

export enum UserRole {
  provider = "provider",
  admin = "admin",
  scheduler = "scheduler",
  patient = "patient",
}

export interface UserProfile {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  phoneNumberVerified?: boolean;
  gender?: Gender;
  organizationId?: string;
  role: UserRole;
  surgeonNpi?: string;
  licenses?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  enabled?: boolean;
  passwordLastUpdatedAt?: string;
  expired?: boolean;
}

export interface AuthEvent {
  signInTime: string;
  event: string;
  signInMethod?: string;
  result?: string;
  device?: string;
}

export interface AuthEventConnection {
  items: AuthEvent[];
  nextToken?: string;
}

export interface UserOrgAssociation {
  userId: string;
  organizationId: string;
  organization_name?: string;
}

export interface CreateUserInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  gender: Gender;
  role: UserRole;
  organizationId?: string;
  surgeonNpi?: string;
  licenses?: string; // needs to be AWSJSON => JSON string, use JSON.stringify() for obj
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: Gender;
  role?: UserRole;
  surgeonNpi?: string;
  licenses?: string; // needs to be AWSJSON => JSON string, use JSON.stringify() for obj
  preferences?: string; // needs to be AWSJSON => JSON string, use JSON.stringify() for obj
}

export interface UserConnection {
  items: UserProfile[];
  nextToken?: string;
}

export interface UserOrgAssociationConnection {
  items: UserOrgAssociation[];
  nextToken?: string;
}

export interface GetUserResponse {
  getUser: UserProfile;
}

export interface ListUsersResponse {
  listUsers: UserConnection;
}

export interface ListUsersByOrgResponse {
  listUsersByOrg: UserConnection;
}

export interface ListOrgsByUserResponse {
  listOrgsByUser: UserOrgAssociationConnection;
}

export interface ListAuthEventsResponse {
  authEvents: AuthEventConnection;
}

export interface CreateUserResponse {
  createUser: UserProfile;
}

export interface UpdateUserResponse {
  updateUser: UserProfile;
}

export interface BulkAddUsersToOrgResponse {
  bulkAddUsersToOrg: UserOrgAssociation[];
}

export interface UpdateUserOrgsResponse {
  updateUserOrgs: UserOrgAssociation[];
}

export interface RemoveUserOrgAssociationResponse {
  removeUserOrgAssociation: { success: boolean; message?: string };
}

export interface ResetUserPasswordResponse {
  resetUserPassword: MutationResponse;
}

export interface ResendWelcomeEmailResponse {
  resendWelcomeEmail: MutationResponse;
}

export interface ResetTemporaryPasswordResponse {
  resetTemporaryPassword: MutationResponse;
}

export interface DisableUserResponse {
  disableUser: MutationResponse;
}

export interface EnableUserResponse {
  enableUser: MutationResponse;
}

export interface DeleteUserResponse {
  deleteUser: MutationResponse;
}

export interface PaginationInput {
  limit: number;
  nextToken: String;
}

export interface MutationResponse {
  success: boolean;
  message?: string;
}

// ---- Patient Notification Preferences (VELYS-518) ----
// Self-service only: no userId argument on either field/mutation below — the
// caller is always the target, enforced server-side. Distinct from
// getUser/updateUser's admin-or-org-member authorization.

export interface NotificationTypePreference {
  email: boolean;
  sms: boolean;
}

export interface NotificationTypePreferenceInput {
  email?: boolean;
  sms?: boolean;
}

export interface PatientPreferences {
  userId: string;
  careplanReminders: NotificationTypePreference;
  actionItemStatusChange: NotificationTypePreference;
  enrollmentUpdated: NotificationTypePreference;
  enrollmentCancelled: NotificationTypePreference;
  photoResponse: NotificationTypePreference;
}

export interface PatientPreferencesInput {
  careplanReminders?: NotificationTypePreferenceInput;
  actionItemStatusChange?: NotificationTypePreferenceInput;
  enrollmentUpdated?: NotificationTypePreferenceInput;
  enrollmentCancelled?: NotificationTypePreferenceInput;
  photoResponse?: NotificationTypePreferenceInput;
}

export interface GetPatientPreferencesResponse {
  getPatientPreferences: PatientPreferences;
}

export interface UpdatePatientPreferencesResponse {
  updatePatientPreferences: PatientPreferences;
}

// ---- Users Domain Queries & Mutations ----
const USER_PROFILE_FIELDS = `
  userId
  firstName
  lastName
  email
  phone
  phoneNumberVerified
  gender
  organizationId
  role
  surgeonNpi
  licenses
  preferences
  createdAt
  updatedAt
  status
  enabled
  passwordLastUpdatedAt
  expired
`;

const USER_ORG_ASSOCIATION_FIELDS = `
  userId
  organizationId
`;

const AUTH_EVENT_FIELDS = `
  signInTime
  event
  signInMethod
  result
  device
`;

const NOTIFICATION_TYPE_PREFERENCE_FIELDS = `
  email
  sms
`;

const PATIENT_PREFERENCES_FIELDS = `
  userId
  careplanReminders {
${NOTIFICATION_TYPE_PREFERENCE_FIELDS}
  }
  actionItemStatusChange {
${NOTIFICATION_TYPE_PREFERENCE_FIELDS}
  }
  enrollmentUpdated {
${NOTIFICATION_TYPE_PREFERENCE_FIELDS}
  }
  enrollmentCancelled {
${NOTIFICATION_TYPE_PREFERENCE_FIELDS}
  }
  photoResponse {
${NOTIFICATION_TYPE_PREFERENCE_FIELDS}
  }
`;

export const GET_USER = `
  query GetUser($userId: ID!) {
    getUser(userId: $userId) {
${USER_PROFILE_FIELDS}
    }
  }
`;

export const LIST_USERS = `
  query ListUsers($query: String, $role: UserRole, $organizationId: ID, $pagination: PaginationInput) {
    listUsers(query: $query, role: $role, organizationId: $organizationId, pagination: $pagination) {
      items {
${USER_PROFILE_FIELDS}
      }
      nextToken
    }
  }
`;

export const LIST_USERS_BY_ORG = `
  query ListUsersByOrg($organizationId: ID!, $pagination: PaginationInput) {
    listUsersByOrg(organizationId: $organizationId, pagination: $pagination) {
      items {
${USER_PROFILE_FIELDS}
      }
      nextToken
    }
  }
`;

export const LIST_ORGS_BY_USER = `
  query ListOrgsByUser($userId: ID!, $pagination: PaginationInput) {
    listOrgsByUser(userId: $userId, pagination: $pagination) {
      items {
${USER_ORG_ASSOCIATION_FIELDS}
      }
      nextToken
    }
  }
`;

export const LIST_AUTH_EVENTS = `
  query ListAuthEvents($userId: ID!, $pagination: PaginationInput) {
    authEvents(userId: $userId, pagination: $pagination) {
      items {
${AUTH_EVENT_FIELDS}
      }
      nextToken
    }
  }
`;

export const GET_PATIENT_PREFERENCES = `
  query GetPatientPreferences {
    getPatientPreferences {
${PATIENT_PREFERENCES_FIELDS}
    }
  }
`;

export const CREATE_USER = `
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
${USER_PROFILE_FIELDS}
    }
  }
`;

export const UPDATE_USER = `
  mutation UpdateUser($userId: ID!, $input: UpdateUserInput!) {
    updateUser(userId: $userId, input: $input) {
${USER_PROFILE_FIELDS}
    }
  }
`;

export const BULK_ADD_USERS_TO_ORG = `
  mutation BulkAddUsersToOrg($organizationId: ID!, $userIds: [ID!]!) {
    bulkAddUsersToOrg(organizationId: $organizationId, userIds: $userIds) {
      ${USER_ORG_ASSOCIATION_FIELDS}
    }
  }
`;

export const UPDATE_USER_ORGS = `
  mutation UpdateUserOrgs($userId: ID!, $orgIds: [ID!]!) {
    updateUserOrgs(userId: $userId, orgIds: $orgIds) {
${USER_ORG_ASSOCIATION_FIELDS}
    }
  }
`;

export const REMOVE_USER_ORG_ASSOCIATION = `
  mutation RemoveUserOrgAssociation($userId: ID!, $organizationId: ID!) {
    removeUserOrgAssociation(userId: $userId, organizationId: $organizationId) {
      success
      message
    }
  }
`;

export const RESET_USER_PASSWORD = `
  mutation ResetUserPassword($userId: ID!) {
    resetUserPassword(userId: $userId) {
      success
      message
    }
  }
`;

export const RESEND_WELCOME_EMAIL = `
  mutation ResendWelcomeEmail($userId: ID!) {
    resendWelcomeEmail(userId: $userId) {
      success
      message
    }
  }
`;

export const RESET_TEMPORARY_PASSWORD = `
  mutation ResetTemporaryPassword($userId: ID!) {
    resetTemporaryPassword(userId: $userId) {
      success
      message
    }
  }
`;

export const DISABLE_USER = `
  mutation DisableUser($userId: ID!) {
    disableUser(userId: $userId) {
      success
      message
    }
  }
`;

export const ENABLE_USER = `
  mutation EnableUser($userId: ID!) {
    enableUser(userId: $userId) {
      success
      message
    }
  }
`;

export const DELETE_USER = `
  mutation DeleteUser($userId: ID!) {
    deleteUser(userId: $userId) {
      success
      message
    }
  }
`;

export const UPDATE_PATIENT_PREFERENCES = `
  mutation UpdatePatientPreferences($preferences: PatientPreferencesInput!) {
    updatePatientPreferences(preferences: $preferences) {
${PATIENT_PREFERENCES_FIELDS}
    }
  }
`;

// ---- Users Domain Subscriptions ----
export const ON_CREATE_USER = `
  subscription OnCreateUser {
    onCreateUser {
${USER_PROFILE_FIELDS}
    }
  }
`;

export const ON_UPDATE_USER = `
  subscription OnUpdateUser($userId: ID!) {
    onUpdateUser(userId: $userId) {
${USER_PROFILE_FIELDS}
    }
  }
`;
